// Public preview page — read-only walkthrough of all main features.
// Uses canned demoData.js, so it never hits the backend.

import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, Users, Clock, ListChecks, Calendar, ClipboardList, ArrowLeft, ArrowRight, Lock, Sparkles } from "lucide-react";
import Logo from "../../components/Logo";
import { fmtWeekday, fmtDateMed, fmtRange } from "../../lib/dates";
import {
  DEMO_EMPLOYEES,
  DEMO_SHIFTS,
  DEMO_RULES,
  DEMO_OPERATIONS,
  DEMO_ROSTER,
  DEMO_TIME_OFF,
  DEMO_HEALTH,
} from "./demoData";

const SECTIONS = [
  { key: "employees",  label: "Employees",  icon: Users },
  { key: "shifts",     label: "Shift Types",icon: Clock },
  { key: "rules",      label: "Rules",      icon: ListChecks },
  { key: "operations", label: "Operations", icon: ClipboardList },
  { key: "roster",     label: "Roster",     icon: Calendar },
];

const ReadOnlyBanner = () => (
  <div className="flex items-center gap-2 px-3 py-2 bg-amber-100 border border-amber-300 text-amber-800 text-xs rounded mb-3" data-testid="preview-readonly-banner">
    <Lock className="w-3.5 h-3.5" />
    <span>You're viewing a read-only demo. Sign up to edit your own data.</span>
  </div>
);

const Section = ({ children, testid }) => (
  <div className="bg-white border border-zinc-200 rounded p-6" data-testid={testid}>{children}</div>
);

const EmployeesPanel = () => (
  <Section testid="preview-employees">
    <h2 className="text-base font-semibold text-zinc-900 mb-3">Employees ({DEMO_EMPLOYEES.length})</h2>
    <ReadOnlyBanner />
    <table className="w-full text-sm">
      <thead><tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
        <th className="py-2">Name</th><th>Email</th><th>Skills</th><th>Hours</th>
      </tr></thead>
      <tbody className="divide-y divide-zinc-100">
        {DEMO_EMPLOYEES.map(e => (
          <tr key={e.employee_id}>
            <td className="py-2 font-medium text-zinc-900">{e.name}</td>
            <td className="text-zinc-600">{e.email}</td>
            <td><div className="flex gap-1 flex-wrap">{e.skills.map(s => <span key={s} className="text-[10px] uppercase px-1.5 py-0.5 bg-zinc-100 text-zinc-700 rounded">{s}</span>)}</div></td>
            <td className="text-zinc-600 tabular-nums">{e.min_hours_per_week}–{e.max_hours_per_week}h</td>
          </tr>
        ))}
      </tbody>
    </table>
  </Section>
);

const ShiftsPanel = () => (
  <Section testid="preview-shifts">
    <h2 className="text-base font-semibold text-zinc-900 mb-3">Shift Types ({DEMO_SHIFTS.length})</h2>
    <ReadOnlyBanner />
    <div className="grid sm:grid-cols-3 gap-3">
      {DEMO_SHIFTS.map(s => (
        <div key={s.shift_id} className="p-3 border border-zinc-200 rounded" style={{ borderLeft: `4px solid ${s.color}` }}>
          <p className="font-medium text-zinc-900">{s.name}</p>
          <p className="text-xs text-zinc-500 tabular-nums">{s.start_time} – {s.end_time}</p>
          <p className="text-xs text-zinc-500 mt-1">Min {s.min_staff}, Max {s.max_staff} • {s.frequency}</p>
        </div>
      ))}
    </div>
  </Section>
);

const RulesPanel = () => (
  <Section testid="preview-rules">
    <h2 className="text-base font-semibold text-zinc-900 mb-3">Rules ({DEMO_RULES.length})</h2>
    <ReadOnlyBanner />
    <div className="space-y-2">
      {DEMO_RULES.map(r => (
        <div key={r.rule_id} className="flex items-center justify-between border border-zinc-200 rounded px-3 py-2">
          <div>
            <p className="text-sm font-medium text-zinc-900">{r.name}</p>
            <p className="text-xs text-zinc-500">{r.rule_type} = {r.value}</p>
          </div>
          <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${r.is_hard ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{r.is_hard ? "Hard" : "Soft"}</span>
        </div>
      ))}
    </div>
  </Section>
);

const OperationsPanel = () => (
  <Section testid="preview-operations">
    <h2 className="text-base font-semibold text-zinc-900 mb-3">Operations ({DEMO_OPERATIONS.length})</h2>
    <ReadOnlyBanner />
    <p className="text-xs text-zinc-500 mb-3">Date-bound staffing requirements that override min_staff for specific days.</p>
    <div className="space-y-2">
      {DEMO_OPERATIONS.map(op => (
        <div key={op.operation_id} className="flex items-center gap-3 border border-zinc-200 rounded px-3 py-2">
          <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: op.color }} />
          <div className="flex-1">
            <p className="text-sm font-medium text-zinc-900">{op.name}</p>
            <p className="text-xs text-zinc-500">{op.dates.map(fmtDateMed).join(", ")} • staff×{op.staff_required}</p>
          </div>
          <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${op.is_hard ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{op.is_hard ? "Hard" : "Soft"}</span>
        </div>
      ))}
    </div>
  </Section>
);

const RosterPanel = () => {
  // Build calendar grid
  const days = (() => {
    const out = [];
    const fmtLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const s = new Date(`${DEMO_ROSTER.start_date}T00:00:00`);
    for (let i = 0; i < 14; i++) { const d = new Date(s); d.setDate(s.getDate() + i); out.push(fmtLocal(d)); }
    return out;
  })();
  const shift = (id) => DEMO_SHIFTS.find(s => s.shift_id === id);
  const op = (date, shift_id) => DEMO_OPERATIONS.find(o => o.shift_id === shift_id && o.dates.includes(date));
  const leave = (eid, date) => DEMO_TIME_OFF.find(t => t.employee_id === eid && t.status === "approved" && t.start_date <= date && t.end_date >= date);

  const entriesByEmpDate = {};
  for (const e of DEMO_ROSTER.entries) {
    const k = `${e.employee_id}|${e.date}`;
    entriesByEmpDate[k] = entriesByEmpDate[k] || [];
    entriesByEmpDate[k].push(e);
  }

  return (
    <Section testid="preview-roster">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold text-zinc-900">{DEMO_ROSTER.name}</h2>
          <p className="text-xs text-zinc-500">{fmtRange(DEMO_ROSTER.start_date, DEMO_ROSTER.end_date)}</p>
        </div>
        <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-2 py-1 rounded" data-testid="preview-roster-status">Published</span>
      </div>
      <ReadOnlyBanner />

      {/* Health */}
      <div className="flex items-center gap-4 bg-emerald-50 border border-emerald-200 rounded p-3 mb-3" data-testid="preview-health-card">
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
          <span className="text-xl font-bold text-emerald-700 tabular-nums">{DEMO_HEALTH.overall}</span>
        </div>
        <div className="flex-1 grid grid-cols-2 sm:grid-cols-5 gap-2">
          {DEMO_HEALTH.categories.map(c => (
            <div key={c.name} className="text-[11px] text-zinc-600">
              <div className="flex justify-between"><span>{c.name}</span><span className="tabular-nums font-semibold">{c.score}</span></div>
              <div className="h-1 bg-emerald-100 rounded overflow-hidden mt-0.5"><div className="h-full bg-emerald-500" style={{ width: `${c.score}%` }} /></div>
            </div>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto -mx-6 px-6">
        <div className="inline-flex border border-zinc-200 rounded text-[10px] min-w-full">
          {/* Left col: employees */}
          <div className="flex-shrink-0 bg-zinc-50 border-r border-zinc-200">
            <div className="h-10 px-2 flex items-center font-semibold text-zinc-500 uppercase tracking-wide border-b border-zinc-200">Employee</div>
            {DEMO_EMPLOYEES.map(e => (
              <div key={e.employee_id} className="h-12 px-2 flex items-center font-medium text-zinc-800 border-b border-zinc-100 whitespace-nowrap">{e.name}</div>
            ))}
          </div>
          {days.map(dateStr => (
            <div key={dateStr} className="flex-shrink-0 w-20 border-r border-zinc-100 last:border-r-0">
              <div className="h-10 px-1 flex flex-col items-center justify-center bg-zinc-50 border-b border-zinc-200">
                <span className="text-[9px] text-zinc-500 uppercase">{fmtWeekday(dateStr)}</span>
                <span className="font-semibold tabular-nums">{dateStr.slice(8, 10)}</span>
              </div>
              {DEMO_EMPLOYEES.map(emp => {
                const list = entriesByEmpDate[`${emp.employee_id}|${dateStr}`] || [];
                const lv = leave(emp.employee_id, dateStr);
                return (
                  <div key={emp.employee_id} className="h-12 p-0.5 border-b border-zinc-100 flex flex-col gap-0.5">
                    {list.map((entry, idx) => {
                      const sh = shift(entry.shift_id);
                      const o = op(dateStr, entry.shift_id);
                      return (
                        <div key={idx} className="text-[8px] leading-tight px-1 py-0.5 rounded relative font-medium"
                          style={o ? { backgroundColor: o.color + "33", borderLeft: `2px solid ${o.color}` } : { backgroundColor: sh.color + "22", borderLeft: `2px solid ${sh.color}` }}>
                          {sh.name}
                          {o && <span className="absolute top-0 right-0 text-[6px] uppercase font-bold px-0.5" style={{ backgroundColor: o.color, color: "white" }}>OP</span>}
                        </div>
                      );
                    })}
                    {list.length === 0 && lv && (
                      <div className="text-[9px] leading-tight px-1 py-0.5 rounded flex items-center justify-center font-bold tracking-wider"
                        style={{ backgroundColor: "#A78BFA33", color: "#A78BFA", border: "1px dashed #A78BFA" }}>
                        A/L
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};

const Preview = () => {
  const navigate = useNavigate();
  const [section, setSection] = useState("roster");

  const renderSection = () => {
    if (section === "employees")  return <EmployeesPanel />;
    if (section === "shifts")     return <ShiftsPanel />;
    if (section === "rules")      return <RulesPanel />;
    if (section === "operations") return <OperationsPanel />;
    return <RosterPanel />;
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="preview-home-link">
            <Logo size={28} />
            <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 hidden sm:inline">/ Preview</span>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/")} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="preview-back-btn">
              <ArrowLeft className="w-3.5 h-3.5" /> Home
            </button>
            <button onClick={() => navigate("/dashboard")} className="px-3 py-1.5 bg-[#002FA7] hover:bg-[#00227A] text-white text-xs rounded inline-flex items-center gap-1" data-testid="preview-signup-btn">
              <Sparkles className="w-3.5 h-3.5" /> Start free trial <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Eye className="w-5 h-5 text-[#002FA7]" />
          <div>
            <h1 className="text-xl font-bold text-zinc-900">Live demo — see the app before you sign up</h1>
            <p className="text-sm text-zinc-500">Read-only. Click the sections below to explore each part of the workflow.</p>
          </div>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap" role="tablist">
          {SECTIONS.map(s => {
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => setSection(s.key)}
                role="tab"
                aria-selected={section === s.key}
                className={`px-3 py-2 text-sm rounded inline-flex items-center gap-2 border transition-colors ${section === s.key ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}
                data-testid={`preview-tab-${s.key}`}
              >
                <Icon className="w-4 h-4" />
                {s.label}
              </button>
            );
          })}
        </div>

        {renderSection()}

        <div className="mt-8 bg-[#002FA7] text-white rounded p-6 flex items-center justify-between flex-wrap gap-3" data-testid="preview-cta">
          <div>
            <p className="font-semibold text-lg">Like what you see?</p>
            <p className="text-sm opacity-80">Start your 30-day free trial — no credit card required.</p>
          </div>
          <button onClick={() => navigate("/dashboard")} className="px-4 py-2 bg-white text-[#002FA7] font-medium rounded inline-flex items-center gap-2" data-testid="preview-cta-btn">
            Start free trial <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Preview;
